import { TOOL_PREP_KEYS } from "../../utils/editorEntrySession";
import { CONVERSION_TOOL_LIST, conversionHowToUse } from "./conversionTools";

/**
 * Normalize how-to content for ToolPrepPopup.
 */
export function normalizeToolPrepHowToUse(value) {
  if (!value || typeof value !== "object") return null;
  const aka = typeof value.aka === "string" ? value.aka.trim() : "";
  const summary =
    typeof value.summary === "string"
      ? value.summary.trim()
      : typeof value.bio === "string"
        ? value.bio.trim()
        : "";
  const steps = Array.isArray(value.steps)
    ? value.steps.map((s) => String(s || "").trim()).filter(Boolean)
    : [];
  const tip = typeof value.tip === "string" ? value.tip.trim() : "";
  if (!aka && !summary && !steps.length && !tip) return null;
  return { aka, summary, steps, tip };
}

/**
 * Build how-to payload from aka/bio plus optional steps/tip.
 */
export function buildToolPrepHowToUse({
  aka = "",
  bio = "",
  summary = "",
  steps = null,
  tip = "",
  defaultSteps = [
    "Upload an image",
    "Adjust the settings on the left",
    "Apply or generate when ready",
  ],
} = {}) {
  const resolvedSteps =
    Array.isArray(steps) && steps.length ? steps : defaultSteps;
  return normalizeToolPrepHowToUse({
    aka,
    summary: summary || bio,
    steps: resolvedSteps,
    tip,
  });
}

/**
 * How-to-use copy for every ToolPrepPopup toolKey.
 * ToolPrepPopup falls back to this when `howToUse` is not passed.
 * Callers may still override with an explicit `howToUse` prop.
 */
const CATALOG = {
  // ——— Editing ———
  [TOOL_PREP_KEYS.RESIZE]: {
    aka: "Image Resizer",
    summary: "Change dimensions with presets or custom size while keeping quality.",
    steps: [
      "Upload an image",
      "Pick a preset or search for a size",
      "Choose Fit or Fill if needed",
      "Apply resize, then Open in Editor or Download",
    ],
    tip: "Fit keeps the whole image; Fill crops to fill the frame.",
  },
  [TOOL_PREP_KEYS.SHADOW]: {
    aka: "Drop Shadow",
    summary: "Add a soft or hard drop shadow so subjects feel grounded.",
    steps: [
      "Upload an image (Remove Background helps for clean edges)",
      "Adjust shadow distance, blur, and opacity",
      "Preview on the right, then Open in Editor",
    ],
    tip: "A cutout subject usually looks more natural with shadow than a full scene.",
  },
  [TOOL_PREP_KEYS.OUTLINE]: {
    aka: "Stroke / Border",
    summary: "Add a colored outline around your subject or image.",
    steps: [
      "Upload an image",
      "Choose outline color, thickness, and style",
      "Preview, then Open in Editor",
    ],
    tip: "Turn on Remove Background for a clean outline around the subject only.",
  },
  [TOOL_PREP_KEYS.BACKGROUND_COLOR]: {
    aka: "Solid Background",
    summary: "Replace the background with a solid color or shade.",
    steps: [
      "Upload an image",
      "Pick a color or shade",
      "Apply background, then Open in Editor or Download",
    ],
    tip: "Works best after background removal for a clean cutout fill.",
  },
  [TOOL_PREP_KEYS.WHITE_BACKGROUND]: {
    aka: "White BG",
    summary: "Place your subject on a clean white background.",
    steps: [
      "Upload an image",
      "Fine-tune the white shade if needed",
      "Apply, then Open in Editor or Download",
    ],
    tip: "Ideal for product shots and marketplace listings.",
  },
  [TOOL_PREP_KEYS.BLACK_BACKGROUND]: {
    aka: "Black BG",
    summary: "Place your subject on a solid black background.",
    steps: [
      "Upload an image",
      "Fine-tune the black shade if needed",
      "Apply, then Open in Editor or Download",
    ],
    tip: "Great for portraits and high-contrast product photos.",
  },
  [TOOL_PREP_KEYS.ROTATE]: {
    aka: "Image Rotate",
    summary: "Rotate your photo to the correct orientation.",
    steps: [
      "Upload an image",
      "Use rotate controls (or presets) on the left",
      "Open in Editor when it looks right",
    ],
    tip: "Combine with Remove Background if you only want the subject rotated cleanly.",
  },
  [TOOL_PREP_KEYS.FLIP]: {
    aka: "Mirror Flip",
    summary: "Flip the image horizontally or vertically.",
    steps: [
      "Upload an image",
      "Toggle horizontal and/or vertical flip",
      "Open in Editor when ready",
    ],
    tip: "Horizontal flip is useful for matching lighting or composition direction.",
  },
  [TOOL_PREP_KEYS.PADDING]: {
    aka: "Image Padding",
    summary: "Add space around the image with a solid or transparent pad.",
    steps: [
      "Upload an image",
      "Set padding amount and color",
      "Open in Editor or Download",
    ],
    tip: "Use padding to meet marketplace or social size frames without stretching.",
  },
  [TOOL_PREP_KEYS.CROP]: {
    aka: "Image Crop",
    summary: "Crop to a free region or aspect ratio.",
    steps: [
      "Upload an image",
      "Drag the crop box on the preview",
      "Confirm crop, then Open in Editor",
    ],
    tip: "Lock an aspect ratio when you need a specific social or print size.",
  },
  [TOOL_PREP_KEYS.CIRCLE_CROP]: {
    aka: "Round Crop",
    summary: "Crop the image into a circular frame.",
    steps: [
      "Upload an image",
      "Position and size the circle on the preview",
      "Confirm, then Open in Editor",
    ],
    tip: "Center faces or logos in the circle for profile-ready results.",
  },
  [TOOL_PREP_KEYS.WATERMARK_MAKER]: {
    aka: "Watermark",
    summary: "Add text or logo watermarks — single, grid, or tiled.",
    steps: [
      "Upload an image",
      "Enter text or upload a logo",
      "Set position, opacity, and pattern",
      "Open in Editor or Download",
    ],
    tip: "Lower opacity and tile mode protect the full image without covering the subject.",
  },
  [TOOL_PREP_KEYS.BLUR]: {
    aka: "Photo Blur",
    summary:
      "All blur styles in one place — whole image, or subject only with Remove Background.",
    steps: [
      "Upload an image",
      "Pick a blur style and adjust amount",
      "Optional: Remove Background to blur subject only",
      "Apply blur when ready",
    ],
    tip: "Subject-only blur needs a cutout — turn on Remove Background first.",
  },
  [TOOL_PREP_KEYS.GAUSSIAN_BLUR]: {
    aka: "Soft Blur",
    summary:
      "Soft Gaussian blur with mix, edge preserve, and full / subject / background targeting.",
    steps: [
      "Upload an image",
      "Choose Full, Subject, or Background",
      "Adjust Amount, Mix, and Edge preserve",
      "Apply Gaussian Blur",
    ],
    tip: "Subject and Background targets use a cutout automatically when needed.",
  },
  [TOOL_PREP_KEYS.PIXEL_BLUR]: {
    aka: "Pixelate",
    summary:
      "Hex, square, circle, or diamond mosaic with cell size, gap, softness, and mix.",
    steps: [
      "Upload an image",
      "Pick a pixel shape and adjust size / gap / softness",
      "Apply Pixel Blur",
    ],
    tip: "Use higher mix for a stronger mosaic; lower mix softens the effect.",
  },
  [TOOL_PREP_KEYS.MOTION_BLUR]: {
    aka: "Directional Blur",
    summary:
      "Directional motion blur for speed, action, and dynamic product shots.",
    steps: [
      "Upload an image",
      "Adjust amount and direction",
      "Apply Motion Blur",
    ],
    tip: "Match blur direction to the subject’s movement for a natural look.",
  },
  [TOOL_PREP_KEYS.BOKEH_BLUR]: {
    aka: "Depth Blur",
    summary:
      "Creamy bokeh-style blur for portraits, products, and dreamy depth of field.",
    steps: [
      "Upload an image",
      "Adjust bokeh amount and softness",
      "Apply Bokeh Blur",
    ],
    tip: "Works best when the subject is clearly separated from the background.",
  },
  [TOOL_PREP_KEYS.BACKGROUND_BLUR]: {
    aka: "Background Soften",
    summary: "Keep the subject sharp while softening the background.",
    steps: [
      "Upload an image",
      "Adjust blur strength",
      "Apply Background Blur",
    ],
    tip: "A clear subject cutout improves edge quality around hair and fine detail.",
  },

  // ——— Documents ———
  [TOOL_PREP_KEYS.PDF_WATERMARKER]: {
    aka: "PDF Stamp",
    summary: "Add a text or image watermark across PDF pages.",
    steps: [
      "Upload a PDF",
      "Set watermark text or image, position, and opacity",
      "Preview pages, then download or finish",
    ],
    tip: "Use diagonal placement and lower opacity so content stays readable.",
  },
  [TOOL_PREP_KEYS.WORD_WATERMARKER]: {
    aka: "Word Stamp",
    summary: "Add a watermark to a Word (.docx) document.",
    steps: [
      "Upload a Word document",
      "Configure watermark text or image",
      "Apply and download the watermarked file",
    ],
    tip: "Keep wording short — long watermarks can crowd page content.",
  },
  [TOOL_PREP_KEYS.PDF_PAGE_NUMBERS]: {
    aka: "Page Numbering",
    summary: "Add page numbers to a PDF with format and position options.",
    steps: [
      "Upload a PDF",
      "Choose number format, position, and start page",
      "Preview, then download",
    ],
    tip: "Preview a middle page to confirm position before exporting.",
  },
  [TOOL_PREP_KEYS.SIGN_PDF]: {
    aka: "eSign PDF",
    summary: "Place signatures, initials, dates, and fields on a PDF.",
    steps: [
      "Upload a PDF",
      "Add signature or field blocks from the left panel",
      "Position them on the page preview",
      "Finish and download the signed PDF",
    ],
    tip: "Create your signature once, then reuse it across pages and documents.",
  },

  // ——— AI tools ———
  [TOOL_PREP_KEYS.AI_EXPAND]: {
    aka: "AI Outpaint",
    summary: "Expand the canvas; AI fills new edges so the scene continues.",
    steps: [
      "Upload an image",
      "Pick an aspect ratio or expand sides",
      "Tap Generate to fill the new edges",
    ],
    tip: "Larger expands need more credits — start with a modest size.",
  },
  [TOOL_PREP_KEYS.AI_RECOLOR]: {
    aka: "Object Recolor",
    summary: "Recolor objects and regions with AI color and prompts.",
    steps: [
      "Upload an image",
      "Describe what to recolor in the prompt",
      "Paint a mask over the area on the preview",
      "Pick a color, then tap Generate",
    ],
    tip: "Use Brush for freehand areas and Box for rectangular regions.",
  },
  [TOOL_PREP_KEYS.AI_ENHANCE]: {
    aka: "Image Enhancer",
    summary: "Improve sharpness and detail without changing the scene.",
    steps: [
      "Upload an image",
      "Pick a strength and optional prompt",
      "Tap Generate when ready",
    ],
    tip: "Start lighter if you want natural detail without looking over-processed.",
  },
  [TOOL_PREP_KEYS.AI_BACKGROUND_IMAGE]: {
    aka: "AI Background Generator",
    summary: "Replace or generate a new background behind your subject.",
    steps: [
      "Upload a subject photo",
      "Describe the background (or pick a template)",
      "Optional: attach a reference image",
      "Tap Generate when ready",
    ],
    tip: "Remove Background first for cleaner subject edges.",
  },
  [TOOL_PREP_KEYS.AI_SHADOW]: {
    aka: "AI Drop Shadow",
    summary:
      "Add a natural drop shadow so products feel grounded and dimensional.",
    steps: [
      "Upload a product or subject cutout",
      "Adjust shadow direction and softness if available",
      "Tap Generate",
    ],
    tip: "Clean cutouts produce the most realistic contact shadows.",
  },
  [TOOL_PREP_KEYS.AI_REMOVE_OBJECT]: {
    aka: "Object Eraser",
    summary: "Erase unwanted objects and fill the gap so the scene looks natural.",
    steps: [
      "Upload an image",
      "Paint or box the object to remove on the preview",
      "Tap Generate to fill the gap",
    ],
    tip: "Cover the whole object — uncovered edges can leave remnants.",
  },
  [TOOL_PREP_KEYS.AI_REMOVE_TEXT]: {
    aka: "Text Remover",
    summary: "Clean text and overlays while reconstructing what’s underneath.",
    steps: [
      "Upload an image",
      "Mark the text to remove on the preview",
      "Tap Generate",
    ],
    tip: "Mark tightly around letters for cleaner reconstruction.",
  },
  [TOOL_PREP_KEYS.AI_UPSCALE]: {
    aka: "Image Upscaler",
    summary: "Increase resolution and recover detail for sharper large images.",
    steps: [
      "Upload an image",
      "Choose upscale factor if available",
      "Tap Generate",
    ],
    tip: "Best results come from photos that are slightly soft, not heavily compressed.",
  },
  [TOOL_PREP_KEYS.AI_RELIGHT]: {
    aka: "AI Lighting",
    summary: "Change lighting and mood without reshooting.",
    steps: [
      "Upload an image",
      "Pick a lighting style or describe the look",
      "Tap Generate",
    ],
    tip: "Subtle relight often looks more natural than extreme mood shifts.",
  },
  [TOOL_PREP_KEYS.AI_REMOVE_WATERMARK]: {
    aka: "Watermark Remover",
    summary: "Remove watermarks and logos, then reconstruct the image underneath.",
    steps: [
      "Upload an image",
      "Mark the watermark area if prompted",
      "Tap Generate",
    ],
    tip: "Only remove watermarks you have rights to clear.",
  },
  [TOOL_PREP_KEYS.AI_BACKGROUND_REMOVER]: {
    aka: "BG Remover",
    summary: "Remove the background cleanly and keep a sharp cutout.",
    steps: [
      "Upload an image",
      "Tap Generate to remove the background",
      "Download or continue editing",
    ],
    tip: "High-contrast subjects against busy backgrounds may need a second pass.",
  },
  [TOOL_PREP_KEYS.AI_SHADOW_REMOVER]: {
    aka: "Shadow Cleaner",
    summary: "Clean unwanted shadows and even out the lighting.",
    steps: [
      "Upload an image",
      "Tap Generate to clean shadows",
      "Review and download",
    ],
    tip: "Works best on flat product shots with soft cast shadows.",
  },
  [TOOL_PREP_KEYS.AI_OLD_PHOTO_RESTORE]: {
    aka: "Photo Restorer",
    summary: "Repair scratches, fade, and noise in old photos.",
    steps: [
      "Upload an old or damaged photo",
      "Tap Generate to restore",
      "Download the result",
    ],
    tip: "Scan or photograph the original flat and well-lit before uploading.",
  },
  [TOOL_PREP_KEYS.AI_COLORIZER]: {
    aka: "B&W Colorizer",
    summary: "Bring black-and-white photos to life with natural color.",
    steps: [
      "Upload a black-and-white photo",
      "Tap Generate to colorize",
      "Download the result",
    ],
    tip: "Higher-resolution scans colorize more convincingly.",
  },
  [TOOL_PREP_KEYS.AI_UNBLUR]: {
    aka: "Deblur",
    summary: "Sharpen soft or motion-blurred photos with AI.",
    steps: [
      "Upload a soft or blurry photo",
      "Tap Generate to unblur",
      "Download the result",
    ],
    tip: "Mild blur recovers better than severe motion smear.",
  },
  [TOOL_PREP_KEYS.AI_REPLACE_OBJECT]: {
    aka: "Object Replacer",
    summary: "Swap an object for something new while matching the scene.",
    steps: [
      "Upload an image",
      "Mask the object to replace",
      "Describe what should replace it",
      "Tap Generate",
    ],
    tip: "Describe size and style so the new object matches the scene lighting.",
  },
  [TOOL_PREP_KEYS.AI_REPLACE_TEXT]: {
    aka: "Text Replacer",
    summary: "Change text in the image and rebuild the background.",
    steps: [
      "Upload an image",
      "Mark the text to change",
      "Enter the new text",
      "Tap Generate",
    ],
    tip: "Match original casing and length when possible for a natural fit.",
  },
  [TOOL_PREP_KEYS.AI_FACE_SWAP]: {
    aka: "Face Swapper",
    summary: "Swap faces between photos with a natural blend.",
    steps: [
      "Upload the base photo",
      "Provide the face to swap in",
      "Tap Generate",
    ],
    tip: "Similar head angle and lighting between faces produces the best blend.",
  },

  // ——— AI Photo Studio ———
  [TOOL_PREP_KEYS.AI_PASSPORT_PHOTO_MAKER]: {
    aka: "AI Passport Photo Maker",
    summary: "Create official passport and ID photos with AI.",
    steps: [
      "Upload a clear portrait photo",
      "Choose country / size and quality",
      "Generate your passport photo",
      "Choose a format and download",
    ],
    tip: "Use even lighting and a plain background for the best compliance score.",
  },

  // ——— Image Conversion ———
  ...Object.fromEntries(
    CONVERSION_TOOL_LIST.map((tool) => [tool.key, conversionHowToUse(tool.key)])
  ),
};

/**
 * @param {string} toolKey
 * @returns {{ aka: string, summary: string, steps: string[], tip: string } | null}
 */
export function getToolPrepHowToUse(toolKey) {
  if (!toolKey) return null;
  return normalizeToolPrepHowToUse(CATALOG[toolKey] || null);
}

/**
 * Merge explicit howToUse over catalog defaults for a toolKey.
 * Explicit non-empty fields win; missing steps/tip fall back to catalog.
 */
export function resolveToolPrepHowToUse(toolKey, howToUse = null) {
  const catalog = getToolPrepHowToUse(toolKey);
  const explicit = normalizeToolPrepHowToUse(howToUse);
  if (!explicit && !catalog) return null;
  if (!explicit) return catalog;
  if (!catalog) return explicit;
  return normalizeToolPrepHowToUse({
    aka: explicit.aka || catalog.aka,
    summary: explicit.summary || catalog.summary,
    steps: explicit.steps.length ? explicit.steps : catalog.steps,
    tip: explicit.tip || catalog.tip,
  });
}

export default CATALOG;
